/**
 * Token custody.
 *
 * The access token lives in MEMORY only. A token in localStorage is readable by
 * any script that reaches the page, and this product's tokens carry capability
 * claims for an account that can move a machine and approve a bill.
 *
 * The refresh token is persisted, because a session must survive a reload — it
 * is stored under its own key and is the only credential written to disk. WHERE
 * it is written is the platform's business: a browser has localStorage, a phone
 * has an OS keychain, and the keychain is meaningfully better. That choice is
 * injected through the runtime rather than assumed here.
 *
 * NOTHING here logs either token.
 */
import { apiRuntime } from './runtime.js';
const REFRESH_KEY = 'erm.rt';
const session = { accessToken: null, expiresAt: 0 };
const listeners = new Set();
function notify() {
    for (const listener of listeners)
        listener();
}
export function subscribeToAuth(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
export function setTokens(pair) {
    session.accessToken = pair.accessToken;
    // Refresh a little early so an in-flight request never rides an expired token.
    session.expiresAt = Date.now() + Math.max(0, pair.expiresIn - 30) * 1000;
    try {
        apiRuntime().secureStore.set(REFRESH_KEY, pair.refreshToken);
    }
    catch {
        // Storage blocked or unavailable — the session lives only for this process.
    }
    notify();
}
export function getAccessToken() {
    return session.accessToken;
}
export function getRefreshToken() {
    try {
        return apiRuntime().secureStore.get(REFRESH_KEY);
    }
    catch {
        return null;
    }
}
export function accessTokenExpired() {
    return session.accessToken === null || Date.now() >= session.expiresAt;
}
export function clearTokens() {
    session.accessToken = null;
    session.expiresAt = 0;
    try {
        apiRuntime().secureStore.remove(REFRESH_KEY);
    }
    catch {
        // Nothing to clear.
    }
    notify();
}
/** A refresh token alone is enough to restore a session on reload. */
export function hasSession() {
    return session.accessToken !== null || getRefreshToken() !== null;
}
//# sourceMappingURL=auth.js.map